import { z } from 'zod'
import { Brand } from '../../shared'

export const ReactivationCampaignEligibilityReason = {
  no_active_campaign: 'no_active_campaign',
  not_enrolled: 'not_enrolled',
  outside_window: 'outside_window',
  already_redeemed: 'already_redeemed',
  not_cancelled: 'not_cancelled',
} as const

export type ReactivationCampaignEligibilityReason =
  typeof ReactivationCampaignEligibilityReason[keyof typeof ReactivationCampaignEligibilityReason]

export const BulkEnrollOutcome = {
  enrolled: 'enrolled',
  duplicate: 'duplicate',
  invalid_user_id: 'invalid_user_id',
  no_subscription: 'no_subscription',
} as const

export const ReactivationCampaignSchema = z.object({
  id: z.string().uuid(),
  campaignKey: z.string(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  discountId: z.string().uuid(),
  brand: z.nativeEnum(Brand),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

/** ISO-8601 instants for campaign window bounds (runbook uses Australia/Sydney `+10:00`). */
const reactivationCampaignInstantSchema = z.string().datetime({ offset: true })

export const CreateReactivationCampaignBodySchema = z
  .object({
    campaignKey: z.string().min(1).max(128),
    startsAt: reactivationCampaignInstantSchema,
    endsAt: reactivationCampaignInstantSchema,
    discountId: z.string().uuid(),
    brand: z.literal(Brand.LYKA).optional().default(Brand.LYKA),
  })
  .refine(({ startsAt, endsAt }) => new Date(startsAt) < new Date(endsAt), {
    message: 'startsAt must be before endsAt',
    path: ['startsAt'],
  })

export const BulkEnrollReactivationCampaignBodySchema = z.object({
  userIds: z.array(z.string()).min(1).max(500),
})

export const BulkEnrollReactivationCampaignResultSchema = z.object({
  userId: z.string(),
  outcome: z.nativeEnum(BulkEnrollOutcome),
})

export const BulkEnrollReactivationCampaignResponseSchema = z.object({
  results: BulkEnrollReactivationCampaignResultSchema.array(),
})

const EligibleReactivationCampaignEligibilitySchema = z.object({
  eligible: z.literal(true),
  discountId: z.string().uuid(),
  enrollmentId: z.string().uuid(),
  campaignKey: z.string(),
})

const IneligibleReactivationCampaignEligibilitySchema = z.object({
  eligible: z.literal(false),
  reason: z.nativeEnum(ReactivationCampaignEligibilityReason),
})

export const ReactivationCampaignEligibilitySchema = z.discriminatedUnion('eligible', [
  EligibleReactivationCampaignEligibilitySchema,
  IneligibleReactivationCampaignEligibilitySchema,
])

export const ClaimReactivationRedemptionResponseSchema = z.object({
  enrollmentId: z.string().uuid(),
  redemptionOperationId: z.string().uuid(),
  redemptionOperationStatus: z.string(),
  created: z.boolean(),
})

export const BindReactivationRedemptionTargetBodySchema = z.object({
  subscriptionId: z.string().uuid(),
  orderId: z.string().uuid(),
})

export const BindReactivationRedemptionTargetResponseSchema = z.object({
  enrollmentId: z.string().uuid(),
  redemptionOperationId: z.string().uuid(),
  redemptionOperationStatus: z.string(),
  subscriptionId: z.string().uuid(),
  orderId: z.string().uuid(),
})

export const CompleteReactivationRedemptionResponseSchema = z.object({
  enrollmentId: z.string().uuid(),
  redeemedAt: z.string().datetime(),
  alreadyCompleted: z.boolean(),
})
